import React, { useState } from "react";
import axios from "axios";
import toast from "react-hot-toast";
import { Modal } from "react-bootstrap";
import { useModalDismiss } from "./useModalDismiss";

type Mode = "edit" | "update_request";

interface Props {
    pageId: string;
    pageTitle: string;
    currentBody: string | null;
    onSave: () => void;
    onCancel: () => void;
}

export default function SuggestEditForm({ pageId, pageTitle, currentBody, onSave, onCancel }: Props) {
    const [mode, setMode] = useState<Mode>("edit");
    const [body, setBody] = useState(currentBody || "");
    const [note, setNote] = useState("");
    const [saving, setSaving] = useState(false);
    const { show, dismiss, onExited } = useModalDismiss(onCancel, onSave);

    function handleSubmit(e: React.FormEvent) {
        e.preventDefault();
        const n = note.trim();
        const b = body.trim();
        if (mode === "edit" && !b) { toast.error("Please describe your suggested changes"); return; }
        if (mode === "update_request" && !n) { toast.error("Please explain what needs updating"); return; }

        setSaving(true);
        axios.post(`/api/manuals/pages/${pageId}/edit-proposals`, {
            proposalType: mode,
            proposedBody: mode === "edit" ? b : null,
            note: n || null,
        })
            .then(res => {
                if (res.data.success) {
                    toast.success(mode === "edit" ? "Edit suggested" : "Update requested");
                    dismiss("save");
                } else {
                    toast.error(res.data.message || "Failed to submit");
                    setSaving(false);
                }
            })
            .catch(err => {
                toast.error(err.response?.data?.message || "Failed to submit");
                setSaving(false);
            });
    }

    return (
        <Modal show={show} onHide={() => dismiss("cancel")} onExited={onExited} centered size="lg" scrollable backdropClassName="m-modal-backdrop">
            <form onSubmit={handleSubmit}>
                <Modal.Header closeButton>
                    <Modal.Title style={{ fontSize: 17, fontWeight: 600 }}>Suggest an edit</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p style={{ color: "#64748b", fontSize: 13, marginTop: -4, marginBottom: 16 }}>
                        On <strong>{pageTitle}</strong>. An admin or the page owner will review your suggestion.
                    </p>

                    <div className="m-suggest-mode">
                        <button
                            type="button"
                            className={`m-suggest-mode-btn${mode === "edit" ? " active" : ""}`}
                            onClick={() => setMode("edit")}
                        >
                            Edit the text
                        </button>
                        <button
                            type="button"
                            className={`m-suggest-mode-btn${mode === "update_request" ? " active" : ""}`}
                            onClick={() => setMode("update_request")}
                        >
                            Request an update
                        </button>
                    </div>

                    {mode === "edit" ? (
                        <>
                            <div className="mb-3">
                                <label className="m-label">Your suggested body</label>
                                <textarea
                                    className="m-input"
                                    rows={12}
                                    value={body}
                                    onChange={e => setBody(e.target.value)}
                                    placeholder="Paste or edit the page body..."
                                    autoFocus
                                />
                                <div style={{ color: "#94a3b8", fontSize: 12, marginTop: 4 }}>
                                    Tip: The current body is pre-filled. Make your changes inline.
                                </div>
                            </div>
                            <div className="mb-3">
                                <label className="m-label">Note (optional)</label>
                                <textarea
                                    className="m-input"
                                    rows={2}
                                    value={note}
                                    onChange={e => setNote(e.target.value)}
                                    placeholder="What changed and why?"
                                />
                            </div>
                        </>
                    ) : (
                        <div className="mb-3">
                            <label className="m-label">What needs updating?</label>
                            <textarea
                                className="m-input"
                                rows={5}
                                value={note}
                                onChange={e => setNote(e.target.value)}
                                placeholder="e.g. The expense limit changed in April; this page still says the old number."
                                autoFocus
                            />
                        </div>
                    )}
                </Modal.Body>
                <Modal.Footer>
                    <button type="button" className="m-btn m-btn-ghost" onClick={() => dismiss("cancel")}>Cancel</button>
                    <button type="submit" className="m-btn m-btn-primary" disabled={saving}>
                        {saving ? "Submitting..." : "Submit"}
                    </button>
                </Modal.Footer>
            </form>
        </Modal>
    );
}
